/* SupplierInvoicesCrudCard.tsx
    Une card affichant la gestion des factures fournisseurs
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Inclut une barre de progression dépenses/montant pour visualiser l'avancement de la couverture
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import {
	SupplierInvoiceData,
	supplierInvoicesApi,
} from 'features/supplierInvoices';
import { faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface SupplierInvoicesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// Traduction des statuts de facture fournisseur
const STATUS_LABELS: Record<string, string> = {
	DRAFT: 'Brouillon',
	ON_HOLD: 'Bloquée',
	READY: 'Prête',
	SENT: 'Envoyée',
	CANCELED: 'Annulée',
};

/** Formatte un montant en euros (fr-FR) */
const formatCurrency = (value: number | null | undefined): string =>
	new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
		Number(value ?? 0)
	);

/**
 * Calcule le total des dépenses HT associées à une facture fournisseur.
 * Les champs `amountHtInvoiced` arrivent sérialisés en string depuis l'API Doctrine → toujours Number().
 */
const computeTotalExpenses = (invoice: SupplierInvoiceData): number =>
	(invoice.expenses ?? []).reduce(
		(sum, e) => sum + Number(e.amountHtInvoiced ?? 0),
		0
	);

// SupplierInvoicesCrudCard component
export const SupplierInvoicesCrudCard: React.FC<
	SupplierInvoicesCrudCardProps
> = ({
	size = 'sm',
	returnPath = '/app/supplierInvoices',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: SupplierInvoiceData) => a.id }]
			: []),
		// Colonne Référence interne
		{
			label: 'Réf. interne',
			field: 'referenceNumber',
			render: (a: SupplierInvoiceData) => a.referenceNumber ?? '',
			exportValue: (a: SupplierInvoiceData): string => a.referenceNumber ?? '',
		},
		// Colonne Référence fournisseur
		{
			label: 'Référence fournisseur',
			field: 'supplierInvoiceReference',
			render: (a: SupplierInvoiceData) => a.supplierInvoiceReference ?? '',
			exportValue: (a: SupplierInvoiceData): string =>
				a.supplierInvoiceReference ?? '',
		},
		// Colonne "Date de facture" au format jour/mois/année
		{
			label: 'Date de facture',
			field: 'supplierInvoiceDate',
			filterType: 'date',
			render: (a: SupplierInvoiceData) =>
				a.supplierInvoiceDate
					? new Date(a.supplierInvoiceDate).toLocaleDateString('fr-FR')
					: '—',
			exportValue: (a: SupplierInvoiceData): string =>
				a.supplierInvoiceDate
					? new Date(a.supplierInvoiceDate).toLocaleDateString('fr-FR')
					: '',
		},
		// Colonne "Montant HT" de la facture fournisseur
		{
			label: 'Montant HT',
			field: 'amount',
			render: (a: SupplierInvoiceData) => formatCurrency(a.amount),
			exportValue: (a: SupplierInvoiceData): number => Number(a.amount ?? 0),
		},
		// Colonne "Dépenses HT" : somme des dépenses associées + barre de progression
		{
			label: 'Dépenses HT',
			field: 'none',
			render: (a: SupplierInvoiceData): React.ReactNode => {
				const totalExpenses = computeTotalExpenses(a);
				const montant = Number(a.amount ?? 0);
				const pct =
					montant > 0 ? Math.round((totalExpenses / montant) * 100) : null;
				// Couleur de la barre basée sur les montants réels (pas le % arrondi)
				const barColor =
					montant <= 0
						? 'bg-gray-300'
						: totalExpenses > montant
							? 'bg-red-500'
							: totalExpenses < montant
								? 'bg-orange-400'
								: 'bg-green-500';
				const textColor =
					montant <= 0 || pct === null
						? 'text-gray-400'
						: totalExpenses > montant
							? 'text-red-500'
							: totalExpenses < montant
								? 'text-orange-500'
								: 'text-green-600';
				return (
					<div className="flex flex-col gap-0.5 min-w-[110px]">
						{/* Montant dépenses + pourcentage */}
						<div className="flex justify-between text-xs">
							<span>{formatCurrency(totalExpenses)}</span>
							{pct !== null && (
								<span className={`font-semibold ${textColor}`}>{pct}%</span>
							)}
						</div>
						{/* Barre de progression */}
						<div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
							<div
								className={`h-1.5 rounded-full transition-all ${barColor}`}
								style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
							/>
						</div>
					</div>
				);
			},
			exportValue: (a: SupplierInvoiceData): number => computeTotalExpenses(a),
		},
		// Colonne "Fournisseur" uniquement si extended
		...(extended
			? [
					{
						label: 'Fournisseur',
						field: 'supplier.label',
						render: (a: SupplierInvoiceData) => a.supplier?.label ?? '',
						exportValue: (a: SupplierInvoiceData): string =>
							a.supplier?.label ?? '',
						linkUrl: (a: SupplierInvoiceData) =>
							`/app/tiers/${a.supplier?.id}/manage`,
					},
				]
			: []),
		// Colonne "État" uniquement si extended avec traductions
		...(extended
			? [
					{
						label: 'État',
						field: 'status',
						render: (a: SupplierInvoiceData) =>
							STATUS_LABELS[a.status ?? ''] ?? a.status ?? '',
						exportValue: (a: SupplierInvoiceData): string =>
							STATUS_LABELS[a.status ?? ''] ?? a.status ?? '',
					},
				]
			: []),
	];

	// Colonnes visibles uniquement dans l'export Excel
	const exportOnlyColumns = [
		// En mode sm, le fournisseur n'est pas affiché → l'ajouter dans l'export
		...(!extended
			? [
					{
						label: 'Fournisseur',
						exportValue: (a: SupplierInvoiceData): string =>
							a.supplier?.label ?? '',
					},
					{
						label: 'État',
						exportValue: (a: SupplierInvoiceData): string =>
							STATUS_LABELS[a.status ?? ''] ?? a.status ?? '',
					},
				]
			: []),
		// % de couverture des dépenses par rapport au montant facture
		{
			label: '% couverture dépenses',
			exportValue: (a: SupplierInvoiceData): string => {
				const totalExpenses = computeTotalExpenses(a);
				const montant = Number(a.amount ?? 0);
				if (montant <= 0) return '';
				return `${Math.round((totalExpenses / montant) * 100)}%`;
			},
		},
		// Contact interne (responsable de la facture)
		{
			label: 'Contact interne',
			exportValue: (a: SupplierInvoiceData): string => {
				const u = a.internalContact;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		// % pénalités et soins
		{
			label: 'Pénalités / soins (%)',
			exportValue: (a: SupplierInvoiceData): number | string =>
				a.penaltyAndCarePercent != null ? Number(a.penaltyAndCarePercent) : '',
		},
		// Date de création
		{
			label: 'Créé le',
			exportValue: (a: SupplierInvoiceData): string =>
				a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : '',
		},
	];

	return (
		<GenericCrudCard<SupplierInvoiceData>
			queryKey="supplierInvoices"
			title={
				size === 'sm'
					? 'Factures fournisseurs'
					: 'Liste des factures fournisseurs'
			}
			fetchData={(token, options) =>
				supplierInvoicesApi.listPaginated(token, {
					...options,
					sort: options?.sort || 'referenceNumber',
					order: options?.order || 'DESC',
				})
			}
			deleteItem={supplierInvoicesApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter une facture fournisseur"
			getAddPath={() => '/app/supplierInvoices/new'}
			getEditPath={(id) => `/app/supplierInvoices/${id}/edit`}
			emptyMessage="Aucune facture fournisseur trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faFileInvoiceDollar : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default SupplierInvoicesCrudCard;
